import { useMsal } from "@azure/msal-react";
import { useGiggedApiSWR } from "../api";
import { UserClaimDto, UserGetMeDto } from "../models/app/userMe";

export type UseCurrentUserReturn = {
    userGivenName: string
    userFamilyName: string
    email: string
    userRole: "client" | "talent" | undefined
    talentId?: string
    talentTradingName?: string
    clientId?: string
    clientCompanyName?: string
    clientDisplayName?: string
    clientIsVerified?: boolean
    idpUserId: string
    organizationName?: string
    claims?: UserClaimDto[]
    /** Grant row present (api/me). */
    hasCalendarGrant: boolean
    /** Health check says calendar is usable (api/me). */
    isCalendarOperational: boolean
    /** Single guard: both grant present and operational. */
    calendarReady: boolean
    /** Grant exists but health says not operational (reconnect). */
    needsCalendarReconnect: boolean
    /** True until the first `api/me` response arrives; use to avoid flashing connect/reconnect UI. */
    isCurrentUserLoading: boolean
    /** Connected calendar provider (e.g. "google", "microsoft"); null/undefined when not connected. */
    calendarProvider?: string | null
    /** Email of the connected calendar account; null/undefined when not connected. */
    calendarEmail?: string | null
    /** When calendar health was last evaluated server-side (ISO string). */
    lastCalendarHealthCheckedAt?: string | null
    /** Revalidates `api/me` (runs server calendar health). Resolves with updated user data when successful. */
    refetchCurrentUser: () => Promise<UserGetMeDto | undefined>
}

const useCurrentUser = (): UseCurrentUserReturn => {
    const { accounts } = useMsal();
    const idTokenClaims = accounts.length > 0 ? accounts[0].idTokenClaims : undefined;

    const url = "api/me";

    const { data, isLoading, refetch } = useGiggedApiSWR<UserGetMeDto>(url, { disableAutoReValidation: true });

    const talentAccountStatus = idTokenClaims?.extension_talentAccountStatus;
    const clientAccountStatus = idTokenClaims?.extension_giggedClientAccountStatus;
    const selectedAccountType = idTokenClaims?.extension_selectedAccountType;

    const isTalent =
        !!talentAccountStatus && talentAccountStatus !== "None" ||
        selectedAccountType === "talent";

    const isClient =
        !!clientAccountStatus && clientAccountStatus !== "None" ||
        selectedAccountType === "giggedClient";

    const hasCalendarGrant = data?.hasCalendarGrant ?? false;
    // Optimistic default while /me is loading: avoids flashing reconnect UI or blocking CTAs before we know server state.
    const isCalendarOperational = data?.isCalendarOperational ?? true;
    const calendarReady = hasCalendarGrant && isCalendarOperational;
    const needsCalendarReconnect = hasCalendarGrant && !isCalendarOperational;

    return {
        userGivenName: idTokenClaims?.given_name as string || "",
        userFamilyName: idTokenClaims?.family_name as string || "",
        email: idTokenClaims?.email as string || "",
        userRole: isTalent ? "talent" :
            isClient ? "client" : undefined,
        talentId: data?.talentId,
        talentTradingName: data?.talentTradingName,
        clientId: data?.giggedClientId,
        clientCompanyName: data?.giggedClientName,
        clientDisplayName: data?.giggedClientDisplayName,
        clientIsVerified: data?.giggedClientIsVerified,
        idpUserId: idTokenClaims?.sub || "",
        organizationName: data?.organizationName,
        claims: data?.userClaims,
        hasCalendarGrant,
        isCalendarOperational,
        calendarReady,
        needsCalendarReconnect,
        isCurrentUserLoading: isLoading,
        calendarProvider: data?.calendarProvider,
        calendarEmail: data?.calendarEmail,
        lastCalendarHealthCheckedAt: data?.lastCalendarHealthCheckedAt,
        refetchCurrentUser: refetch
    };
};

export default useCurrentUser;